import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Form, Spinner } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { verifyOtpApi } from 'api/auth';
import { handleSuccessfulLogin } from 'utils/tools';
import './otp.scss';

const LENGTH = 6;

/**
 * Six-digit OTP entry. Verifies against the pre-MFA token and, on success,
 * completes the login. Session conflicts are handed back to the parent.
 */
const OtpForm = ({ loginId, mfaToken, onSessionConflict, onBack }) => {
  const [otp, setOtp] = useState(Array(LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputs = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  const focus = (i) => inputs.current[Math.max(0, Math.min(LENGTH - 1, i))]?.focus();

  const setDigit = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    setError('');
    if (value && index < LENGTH - 1) focus(index + 1);
  };

  const onKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) focus(index - 1);
    if (e.key === 'ArrowLeft') focus(index - 1);
    if (e.key === 'ArrowRight') focus(index + 1);
  };

  const onPaste = (e, index) => {
    e.preventDefault();
    const digits = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!digits) return;
    const next = [...otp];
    for (let i = 0; i < digits.length && index + i < LENGTH; i += 1) next[index + i] = digits[i];
    setOtp(next);
    const firstEmpty = next.findIndex((d) => d === '');
    focus(firstEmpty === -1 ? LENGTH - 1 : firstEmpty);
  };

  const verify = async (e) => {
    e.preventDefault();
    const otpCode = otp.join('');
    if (otpCode.length !== LENGTH) {
      setError('Please enter all six digits.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const resp = await verifyOtpApi({ loginId: Number(loginId), otpCode, preMfaToken: mfaToken });
      if (resp?.loginStatus === 'SESSION_CONFLICT') {
        onSessionConflict?.({
          decisionToken: resp.decisionToken,
          existingSessions: resp.existingSessions || [],
          message: resp.message || 'You are already logged in elsewhere.',
        });
        return;
      }
      if (resp?.authToken) {
        handleSuccessfulLogin(resp, navigate);
        return;
      }
      setError(resp?.message || 'That code is not valid. Please try again.');
    } catch (err) {
      setError(err?.response?.data?.message || 'OTP verification failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={verify} className="otp-form">
      <h5 className="mb-1">Two-step verification</h5>
      <p className="text-muted mb-4">Enter the 6-digit code we sent to your preferred device.</p>

      {error && <Alert color="danger" className="py-2">{error}</Alert>}

      <div className="otp-inputs mb-4" onPaste={(e) => onPaste(e, 0)}>
        {otp.map((d, i) => (
          <input
            key={i}
            ref={(el) => {
              inputs.current[i] = el;
            }}
            id={`otp-${i}`}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            className="form-control otp-input"
            value={d}
            aria-label={`Digit ${i + 1}`}
            onChange={(e) => setDigit(e.target.value, i)}
            onKeyDown={(e) => onKeyDown(e, i)}
          />
        ))}
      </div>

      <div className="d-grid">
        <Button color="primary" type="submit" disabled={loading}>
          {loading ? <Spinner size="sm" className="me-2" /> : null}
          Verify and sign in
        </Button>
      </div>

      {onBack && (
        <div className="text-center mt-3">
          <button type="button" className="btn btn-link p-0" onClick={onBack}>
            <i className="mdi mdi-arrow-left me-1" />
            Back to sign in
          </button>
        </div>
      )}
    </Form>
  );
};

OtpForm.propTypes = {
  loginId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  mfaToken: PropTypes.string.isRequired,
  onSessionConflict: PropTypes.func,
  onBack: PropTypes.func,
};

export default OtpForm;
